import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Sidebar } from 'primereact/sidebar';
import { InputText } from 'primereact/inputtext';
import logoImage from '../assets/img/bet_copa_verde_rectangle_transparent.png';
import { Crest } from './common';
import { addDays, capitalizeName, formatKickoffDay, isoDate, parseIsoDate } from '../utils/format';
import { loadSetting, saveSetting } from '../utils/storage';
import { leagueSelectionPath, parseLeagueKeys, toggleLeague } from '../utils/leagues';

// Shortcuts shown under the header; the full list is in the leagues panel.
export const FEATURED_LEAGUES = [
  { label: 'Premier League', area: 'england', league: 'premier-league' },
  { label: 'Champions League', area: 'europe', league: 'champions-league' },
  { label: 'La Liga', area: 'spain', league: 'laliga' },
  { label: 'Serie A', area: 'italy', league: 'serie-a' },
  { label: 'Bundesliga', area: 'germany', league: 'bundesliga' },
  { label: 'Ligue 1', area: 'france', league: 'ligue-1' },
  { label: 'Europa League', area: 'europe', league: 'europa-league' },
  { label: 'Conference League', area: 'europe', league: 'conference-league' },
  { label: 'Copa Libertadores', area: 'south-america', league: 'copa-libertadores' },
  { label: 'FA Cup', area: 'england', league: 'fa-cup' },
  { label: 'EFL Cup', area: 'england', league: 'efl-cup' },
  { label: 'Brazil Serie A', area: 'brazil', league: 'serie-a-betano' },
  { label: 'MLS', area: 'usa', league: 'mls' },
  { label: 'World Cup', area: 'world', league: 'world-cup' },
];

const leaguePath = (area, league) => `/league/${area}/${league}`;

function DateNav({ compact }) {
  const { date } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const today = new Date();
  const current = parseIsoDate(date) || today;
  const go = (day) => navigate(isoDate(day) === isoDate(today) ? '/' : `/date/${isoDate(day)}`);
  const isDayView = Boolean(date) || pathname === '/';
  const isToday = isDayView && isoDate(current) === isoDate(today);
  const isTomorrow = isDayView && isoDate(current) === isoDate(addDays(today, 1));

  return (
    <div className="date-nav" role="group" aria-label="Choose a match day">
      <Button icon="pi pi-chevron-left" text rounded aria-label="Previous day" onClick={() => go(addDays(current, -1))} />
      {!compact && (
        <>
          <Button label="Today" text size="small" aria-pressed={isToday} className={isToday ? 'is-active' : ''}
            onClick={() => go(today)} />
          <Button label="Tomorrow" text size="small" aria-pressed={isTomorrow} className={isTomorrow ? 'is-active' : ''}
            onClick={() => go(addDays(today, 1))} />
        </>
      )}
      <Calendar value={current} onChange={(e) => e.value && go(e.value)} dateFormat="D d M" readOnlyInput
        inputClassName="date-input" aria-label="Match day" touchUI={compact} />
      <Button icon="pi pi-chevron-right" text rounded aria-label="Next day" onClick={() => go(addDays(current, 1))} />
    </div>
  );
}

function LeaguePanel({ visible, onHide, leagues, loadError }) {
  const [query, setQuery] = useState('');
  const recent = loadSetting('recentLeagues', []);
  const groups = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const byArea = new Map();
    (leagues || []).forEach((item) => {
      const text = `${item.Area} ${item.League}`.replace(/-/g, ' ').toLowerCase();
      if (!words.every((w) => text.includes(w))) {
        return;
      }
      if (!byArea.has(item.Area)) {
        byArea.set(item.Area, { area: item.Area, image: item.ImagePath, leagues: [] });
      }
      const group = byArea.get(item.Area);
      if (!group.leagues.includes(item.League)) {
        group.leagues.push(item.League);
      }
    });
    return [...byArea.values()];
  }, [leagues, query]);

  return (
    <Sidebar visible={visible} onHide={onHide} className="league-panel" aria-label="Leagues">
      <h2 className="panel-title">Leagues</h2>
      <span className="p-input-icon-left w-full mb-3">
        <i className="pi pi-search" />
        <InputText value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a league or country"
          className="w-full" aria-label="Find a league or country" autoFocus />
      </span>
      {!query && recent.length > 0 && (
        <section className="panel-group">
          <h3>Recently viewed</h3>
          {recent.map((r) => (
            <NavLink key={`${r.area}/${r.league}`} to={leaguePath(r.area, r.league)} onClick={onHide} className="panel-link">
              {capitalizeName(r.league)} <span className="panel-area">{capitalizeName(r.area)}</span>
            </NavLink>
          ))}
        </section>
      )}
      {loadError && <p className="panel-note">The full league list could not be loaded.</p>}
      {!loadError && leagues === null && <p className="panel-note">Loading leagues…</p>}
      {leagues && groups.length === 0 && <p className="panel-note">No league matches “{query}”.</p>}
      {groups.map((g) => (
        <section key={g.area} className="panel-group">
          <h3><Crest path={g.image} size="sm" /> {capitalizeName(g.area)}</h3>
          {g.leagues.map((league) => (
            <NavLink key={league} to={leaguePath(g.area, league)} onClick={onHide} className="panel-link">
              {capitalizeName(league)}
            </NavLink>
          ))}
        </section>
      ))}
    </Sidebar>
  );
}

const leagueLabel = (key) => {
  const featured = FEATURED_LEAGUES.find((l) => `${l.area}/${l.league}` === key);
  return featured ? featured.label : capitalizeName(key.split('/')[1]);
};

export function AppShell({ leagues, leaguesError, theme, onToggleTheme, isDesktop, children }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const { area, league, date } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Leagues selected in the top bar: the league page's one, or every key of /leagues?l=...
  const selected = useMemo(() => {
    if (area && league) {
      return [`${area}/${league}`];
    }
    return pathname === '/leagues' ? parseLeagueKeys(new URLSearchParams(search).get('l')) : [];
  }, [area, league, pathname, search]);

  useEffect(() => {
    if (area && league) {
      const recent = loadSetting('recentLeagues', []).filter((r) => !(r.area === area && r.league === league));
      saveSetting('recentLeagues', [{ area, league }, ...recent].slice(0, 5));
    }
  }, [area, league]);

  let title = "Today's matches";
  if (area && league) {
    title = capitalizeName(league);
  } else if (selected.length > 1) {
    title = selected.length <= 3 ? selected.map(leagueLabel).join(' · ') : `${selected.length} leagues`;
  } else if (date) {
    const day = parseIsoDate(date);
    title = day ? `Matches · ${formatKickoffDay(day)}` : 'Matches';
  } else if (pathname === '/mypicks') {
    title = 'My picks';
  }

  useEffect(() => {
    document.title = `${title} · Bet Copa Verde`;
  }, [title]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">Skip to content</a>
      <header className="app-header">
        <div className="header-row">
          <Button icon="pi pi-bars" text rounded aria-label="Open the league list" onClick={() => setPanelOpen(true)} />
          <Link to="/" className="brand" aria-label="Bet Copa Verde home">
            <img src={logoImage} alt="Bet Copa Verde" />
          </Link>
          {isDesktop && <DateNav />}
          <nav className="header-actions" aria-label="Account">
            <NavLink to="/mypicks" className={({ isActive }) => `header-link${isActive ? ' is-active' : ''}`}>
              <i className="pi pi-bookmark" aria-hidden="true" /> {isDesktop && 'My picks'}
              {!isDesktop && <span className="sr-only">My picks</span>}
            </NavLink>
            <Button icon={theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'} text rounded onClick={onToggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} />
          </nav>
        </div>
        {!isDesktop && <div className="header-row header-row-dates"><DateNav compact /></div>}
        <nav className="league-chips" aria-label="Popular leagues (select several to combine them)">
          <NavLink to="/" end className={({ isActive }) => `chip${isActive ? ' is-active' : ''}`}>All</NavLink>
          {FEATURED_LEAGUES.map((l) => {
            const key = `${l.area}/${l.league}`;
            const on = selected.includes(key);
            return (
              <button key={key} type="button" className={`chip${on ? ' is-active' : ''}`} aria-pressed={on}
                title={on ? `Remove ${l.label}` : `Add ${l.label}`}
                onClick={() => navigate(leagueSelectionPath(toggleLeague(selected, key)))}>
                {on && <i className="pi pi-check" aria-hidden="true" />}
                {l.label}
              </button>
            );
          })}
          {selected.length > 1 && (
            <button type="button" className="chip chip-clear" onClick={() => navigate('/')}>
              <i className="pi pi-times" aria-hidden="true" /> Clear
            </button>
          )}
        </nav>
      </header>
      <main id="main" className="app-main" tabIndex={-1}>
        <h1 className="page-title">{title}</h1>
        {children}
      </main>
      <footer className="app-footer">
        Bet Copa Verde publishes model predictions for information only. It does not take bets or pay out.
      </footer>
      <LeaguePanel visible={panelOpen} onHide={() => setPanelOpen(false)} leagues={leagues} loadError={leaguesError} />
    </div>
  );
}
